import React, { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../services/api'
import { streamQuery } from '../services/queryStream'
import ChatMessage from '../components/knowledge/ChatMessage'

export default function Knowledge() {
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [initiatives, setInitiatives] = useState([])
  const [selectedInitiativeId, setSelectedInitiativeId] = useState('')
  const [history, setHistory] = useState([])
  const [historyOpen, setHistoryOpen] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const scrollRef = useRef(null)
  const abortRef = useRef(null)

  // Abort an in-flight stream on unmount — otherwise the reader loop keeps
  // the connection open and patches state on an unmounted component.
  useEffect(() => () => abortRef.current?.abort(), [])

  // Load initiatives once for the filter chips.
  useEffect(() => {
    api
      .get('/api/v1/initiatives')
      .then(setInitiatives)
      .catch((err) => setError(err.message || 'Could not load initiatives'))
  }, [])

  const loadHistory = useCallback(() => {
    api
      .get('/api/v1/queries/history')
      .then(setHistory)
      .catch((err) => {
        // History is a secondary panel; don't block the page, but log it.
        console.error('Failed to load query history:', err)
      })
  }, [])

  useEffect(() => {
    loadHistory()
  }, [loadHistory])

  // Auto-scroll to bottom as new messages arrive.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages])

  const handleSubmit = async (e) => {
    e?.preventDefault()
    const question = input.trim()
    if (!question || sending) return
    setInput('')
    setError(null)
    setSending(true)

    const userMessage = { id: `u-${Date.now()}`, role: 'user', text: question }
    const assistantId = `a-${Date.now()}`
    const assistantMessage = {
      id: assistantId,
      role: 'assistant',
      text: '',
      sources: [],
      pending: true,
      error: null,
    }
    setMessages((prev) => [ ...prev, userMessage, assistantMessage ])

    const patchAssistant = (patch) => {
      setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, ...patch } : m)))
    }

    const controller = new AbortController()
    abortRef.current = controller

    try {
      await streamQuery({
        questionText: question,
        initiativeId: selectedInitiativeId || null,
        signal: controller.signal,
        onEvent: (event) => {
          if (event.type === 'sources') {
            patchAssistant({ sources: event.sources || [] })
          } else if (event.type === 'related_catalog_entries') {
            patchAssistant({ relatedCatalogEntries: event.related_catalog_entries || [] })
          } else if (event.type === 'delta') {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? { ...m, text: (m.text || '') + (event.text || '') } : m
              )
            )
          } else if (event.type === 'error') {
            patchAssistant({ error: event.message, pending: false })
          } else if (event.type === 'done') {
            patchAssistant({ pending: false })
          }
        },
      })
      patchAssistant({ pending: false })
      loadHistory()
    } catch (err) {
      if (err.name === 'AbortError') return // unmounted mid-stream; no state to patch
      patchAssistant({ pending: false, error: err.message })
      setError(err.message)
    } finally {
      setSending(false)
    }
  }

  const replayFromHistory = (historicQuery) => {
    setMessages([
      { id: `u-${historicQuery.id}`, role: 'user', text: historicQuery.question_text },
      {
        id: `a-${historicQuery.id}`,
        role: 'assistant',
        text: historicQuery.answer_text || '',
        sources: historicQuery.source_references || [],
        pending: false,
      },
    ])
    setHistoryOpen(false)
  }

  return (
    <div className="flex h-[calc(100vh-7rem)] gap-6 sm:h-[calc(100vh-8rem)] lg:h-[calc(100vh-10rem)]">
      {/* History sidebar — inline column on md+, slide-over drawer below */}
      {historyOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-slate-900/40 md:hidden"
            onClick={() => setHistoryOpen(false)}
            aria-hidden="true"
          />
          <aside className="fixed inset-y-0 left-0 z-50 w-80 max-w-[85vw] overflow-y-auto bg-white p-4 shadow-xl md:static md:z-auto md:w-72 md:max-w-none md:shrink-0 md:rounded-xl md:border md:border-slate-200 md:shadow-none">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">History</h3>
            <button
              type="button"
              onClick={() => setHistoryOpen(false)}
              className="text-xs text-slate-400 hover:text-slate-600"
            >
              Close
            </button>
          </div>
          {history.length === 0 && <p className="text-xs text-slate-400">No past queries.</p>}
          <ul className="space-y-2">
            {history.map((q) => (
              <li key={q.id}>
                <button
                  type="button"
                  onClick={() => replayFromHistory(q)}
                  className="w-full text-left rounded-lg border border-slate-100 bg-slate-50 p-3 text-xs hover:border-accent-300 hover:bg-white transition"
                >
                  <div className="font-medium text-slate-800 line-clamp-2">{q.question_text}</div>
                  <div className="mt-1 text-slate-400">
                    {new Date(q.created_at).toLocaleDateString()}
                  </div>
                </button>
              </li>
            ))}
          </ul>
          </aside>
        </>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Knowledge</h2>
            <p className="mt-1 text-sm text-slate-500">
              Ask anything across what the collective has shared.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setHistoryOpen((v) => !v)}
            className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            {historyOpen ? 'Hide history' : 'History'}
          </button>
        </div>

        {/* Filter chips */}
        {initiatives.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSelectedInitiativeId('')}
              className={`rounded-full px-3 py-1 text-xs font-medium transition border ${
                selectedInitiativeId === ''
                  ? 'border-accent-300 bg-accent-50 text-accent-700'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              All initiatives
            </button>
            {initiatives.map((init) => (
              <button
                key={init.id}
                type="button"
                onClick={() => setSelectedInitiativeId(String(init.id))}
                className={`rounded-full px-3 py-1 text-xs font-medium transition border ${
                  selectedInitiativeId === String(init.id)
                    ? 'border-accent-300 bg-accent-50 text-accent-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                {init.name}
              </button>
            ))}
          </div>
        )}

        {/* Messages */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-6 space-y-4"
        >
          {messages.length === 0 && (
            <div className="h-full flex items-center justify-center text-center">
              <div>
                <p className="text-sm text-slate-500">
                  Ask a question about people, projects, or documents.
                </p>
                <p className="mt-2 text-xs text-slate-400">
                  Try: <em>"What AI tooling is IDEO using?"</em>
                </p>
              </div>
            </div>
          )}
          {messages.map((message) => (
            <ChatMessage key={message.id} message={message} />
          ))}
        </div>

        {error && (
          <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </div>
        )}

        {/* Input */}
        <form onSubmit={handleSubmit} className="mt-4 flex items-center gap-3">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask a question…"
            disabled={sending}
            className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500 disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={!input.trim() || sending}
            className="rounded-xl bg-accent-600 px-5 py-3 text-sm font-medium text-white shadow-sm transition hover:bg-accent-500 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {sending ? 'Thinking…' : 'Send'}
          </button>
        </form>
      </div>
    </div>
  )
}
